import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";

import { CrshmAppHeader } from "@/components/CrshmAppHeader";
import { isAdminEmail } from "@/lib/admin-auth";
import { SessionProvider, useSession } from "@/session/session-context";
import {
  AccountExpertScreen,
  AccountIndexRedirect,
  AccountOrdersScreen,
  AccountProfileScreen,
  AccountPushScreen,
} from "@/screens/account-screens";
import { AdminExpertScreen, AdminHomeScreen, AdminOrdersScreen, AdminPushScreen } from "@/screens/admin-screens";
import { BlogIndexScreen, BlogPostScreen } from "@/screens/blog-screens";
import { AuthCallbackScreen } from "@/screens/auth-callback-screen";
import {
  DashboardScreen,
  DemoHomeScreen,
  DemoOrdersScreen,
  DemoProfileScreen,
  DoOrDumpScreen,
  HomeScreen,
  SignInScreen,
} from "@/screens/misc-screens";

function LoadingShell() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-[#faf8ff] text-sm text-zinc-600">
      Loading…
    </div>
  );
}

function BlogLayout() {
  const { user, loading } = useSession();
  if (loading) return <LoadingShell />;
  return (
    <div className="flex min-h-dvh flex-col bg-[#faf8ff] text-zinc-900">
      <CrshmAppHeader email={user?.email ?? null} />
      <Outlet />
    </div>
  );
}

function SignInLayout() {
  const { user, loading } = useSession();
  if (loading) return <LoadingShell />;
  return (
    <div className="flex min-h-dvh flex-col bg-[#faf8ff] text-zinc-900">
      <CrshmAppHeader email={user?.email ?? null} />
      <Outlet />
    </div>
  );
}

function DoOrDumpLayout() {
  const { user, loading } = useSession();
  if (loading) return <LoadingShell />;
  return (
    <>
      <CrshmAppHeader email={user?.email ?? null} />
      <Outlet />
    </>
  );
}

function DashboardLayout() {
  const { user, loading } = useSession();
  if (loading) return <LoadingShell />;
  if (!user?.email) {
    return <Navigate to={`/sign-in?next=${encodeURIComponent("/dashboard")}`} replace />;
  }
  const showAdminLink = isAdminEmail(user.email);
  return (
    <div className="min-h-dvh bg-[#faf8ff]">
      <CrshmAppHeader email={user.email} showAdminLink={showAdminLink} />
      <Outlet />
    </div>
  );
}

function AccountLayoutShell() {
  const { user, loading } = useSession();
  if (loading) return <LoadingShell />;
  if (!user) {
    return <Navigate to="/dashboard" replace />;
  }
  const showAdminLink = isAdminEmail(user.email ?? "");
  return (
    <div className="min-h-dvh bg-[#faf8ff]">
      <CrshmAppHeader email={user.email ?? null} showAdminLink={showAdminLink} />
      <div className="mx-auto max-w-2xl px-4 py-8 pb-16 sm:px-6 lg:px-8">
        <Outlet />
      </div>
    </div>
  );
}

function AdminLayoutShell() {
  const { user, loading } = useSession();
  if (loading) return <LoadingShell />;
  if (!user?.email) {
    return <Navigate to={`/sign-in?next=${encodeURIComponent("/admin")}`} replace />;
  }
  if (!isAdminEmail(user.email)) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <div className="min-h-dvh bg-[#faf8ff]">
      <CrshmAppHeader email={user.email} showAdminLink />
      <div className="mx-auto max-w-6xl px-4 py-8 pb-16 sm:px-6 lg:px-8">
        <Outlet />
      </div>
    </div>
  );
}

function DemoLayoutShell() {
  const { user, loading } = useSession();
  if (loading) return <LoadingShell />;
  return (
    <div className="min-h-dvh bg-[#faf8ff]">
      <CrshmAppHeader email={user?.email ?? null} />
      <Outlet />
    </div>
  );
}

function DemoAccountLayoutShell() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-8 pb-16 sm:px-6 lg:px-8">
      <Outlet />
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          <Route path="/" element={<HomeScreen />} />
          <Route path="/auth/callback" element={<AuthCallbackScreen />} />

          <Route element={<SignInLayout />}>
            <Route path="/sign-in" element={<SignInScreen />} />
          </Route>

          <Route element={<DoOrDumpLayout />}>
            <Route path="/do-or-dump" element={<DoOrDumpScreen />} />
          </Route>

          <Route element={<BlogLayout />}>
            <Route path="/blog" element={<BlogIndexScreen />} />
            <Route path="/blog/:slug" element={<BlogPostScreen />} />
          </Route>

          <Route element={<DashboardLayout />}>
            <Route path="/dashboard" element={<DashboardScreen />} />
          </Route>

          <Route element={<AccountLayoutShell />}>
            <Route path="/account" element={<AccountIndexRedirect />} />
            <Route path="/account/profile" element={<AccountProfileScreen />} />
            <Route path="/account/orders" element={<AccountOrdersScreen />} />
            <Route path="/account/orders/:orderId/expert" element={<AccountExpertScreen />} />
            <Route path="/account/orders/:orderId/push" element={<AccountPushScreen />} />
          </Route>

          <Route element={<AdminLayoutShell />}>
            <Route path="/admin" element={<AdminHomeScreen />} />
            <Route path="/admin/orders" element={<AdminOrdersScreen />} />
            <Route path="/admin/expert/:orderId" element={<AdminExpertScreen />} />
            <Route path="/admin/push/:orderId" element={<AdminPushScreen />} />
          </Route>

          <Route path="/demo" element={<DemoLayoutShell />}>
            <Route index element={<DemoHomeScreen />} />
            <Route element={<DemoAccountLayoutShell />}>
              <Route path="account/profile" element={<DemoProfileScreen />} />
              <Route path="account/orders" element={<DemoOrdersScreen />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}
